/**
 * Pagination models for server-side paging support
 * Used across admin-v2 components for consistent paging behavior
 */

/**
 * Request parameters for paginated API calls
 */
export interface PagedRequest {
    /** Current page number (1-based) */
    page: number;

    /** Number of items per page */
    pageSize: number;

    /** Field to sort by */
    sortField?: string;

    /** Sort direction */
    sortOrder?: 'asc' | 'desc';

    /** Optional search/filter term */
    searchTerm?: string;
}

/**
 * Generic paginated response from the API
 */
export interface PagedResponse<T> {
    /** Array of items for the current page */
    data: T[];

    /** Total number of records across all pages */
    totalRecords: number;

    /** Current page number (1-based) */
    page: number;

    /** Number of items per page */
    pageSize: number;

    /** Total number of pages */
    totalPages: number;
}

/**
 * Default pagination settings
 */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Available page size options for dropdowns
 */
export const PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100];

/**
 * Helper to create a default PagedRequest
 */
export function createDefaultPagedRequest(overrides?: Partial<PagedRequest>): PagedRequest {
    return {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        sortOrder: 'asc',
        ...overrides,
    };
}

/**
 * Helper to calculate total pages from total records and page size
 */
export function calculateTotalPages(totalRecords: number, pageSize: number): number {
    return Math.ceil(totalRecords / pageSize);
}

/**
 * Helper to check if there's a next page
 */
export function hasNextPage(currentPage: number, totalPages: number): boolean {
    return currentPage < totalPages;
}

/**
 * Helper to check if there's a previous page
 */
export function hasPreviousPage(currentPage: number): boolean {
    return currentPage > 1;
}
